import React, { CSSProperties, memo, useEffect } from "react";

import { CircularProgress, styled } from '@mui/material';
import useResizeObserver from "use-resize-observer";
import { areEqual, FixedSizeGrid, GridChildComponentProps } from "react-window";


const ItemRoot = styled('div')({
  position: 'relative',
})

const Spinner = styled(CircularProgress)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
})


const GridItem: React.FC<GridChildComponentProps> = ({ data, columnIndex, rowIndex, style }) => {
  const index = rowIndex * data.colCount + columnIndex;

  const items = data.items as any[];


  if (data.loadingMore && index + 1 === items.length) {
    return (
      <ItemRoot style={{ ...style, padding: data.spacing }}>
        <Spinner />
      </ItemRoot>
    )
  }

  if (index >= items.length) {
    return null;
  }
  return (
    <ItemRoot style={{ ...style, padding: data.spacing }}>
      {
        data.onRenderItem(items[ index ])
      }
    </ItemRoot>
  )
}

const Root = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  flex: 1,
});


interface InternalState {
  isLoadingMore?: boolean;
  scrollToTop?: number;
  initDone?: boolean;
}


const internalData: { [ id: string ]: InternalState } = {};

const gridRef = React.createRef<FixedSizeGrid>();


interface Props {
  id: string;
  onLoadMore: () => void;
  items: any[];
  itemWidth: number;
  itemHeight: number;
  isLoadingMore?: boolean;
  alignToCenter?: boolean;
  spacing: number;
  onRenderItem: (data: any) => React.ReactNode;
}

const CustomGrid: React.FC<Props> = ({ id, onLoadMore, items, itemWidth, itemHeight, isLoadingMore, alignToCenter, spacing, onRenderItem }) => {

  if (!internalData[ id ]) {
    internalData[ id ] = {};
  }

  const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();


  const itemSpacing = spacing * 2;


  const colCount = Math.max(1, Math.floor(width / (itemWidth + itemSpacing)));
  const itemsCount = items.length > 0 ? items.length + 1 : 0;

  const rowCount = colCount > 0 ? Math.ceil(itemsCount / colCount) : 0;

  const actualWidth = alignToCenter ? colCount * (itemWidth + itemSpacing) : width;

  const loadMore = () => {
    isLoadingMore = true;

    onLoadMore();
  }

  useEffect(() => {
    internalData[ id ].isLoadingMore = false;
    internalData[ id ].initDone = true;

    gridRef.current?.scrollTo({
      scrollTop: internalData[ id ].scrollToTop || 0,
    });


    return () => {
      internalData[ id ].initDone = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style: CSSProperties = {};
  if (alignToCenter) {
    style.display = 'flex';
    style.justifyContent = 'center';
  }


  return (
    <Root style={style} ref={ref}>
      <FixedSizeGrid
        height={height}
        width={actualWidth}
        columnCount={colCount}
        columnWidth={itemWidth + itemSpacing}
        rowCount={rowCount}
        rowHeight={itemHeight + itemSpacing}
        itemData={{
          items: items,
          colCount,
          loadingMore: isLoadingMore,
          spacing,
          onRenderItem,
        }}
        ref={gridRef}
        onItemsRendered={(args) => {
          if (args.visibleRowStopIndex + 1 >= rowCount && !isLoadingMore) {
            loadMore();
          }
        }}
        onScroll={(args) => {
          if (internalData[ id ].initDone) {
            internalData[ id ].scrollToTop = args.scrollTop;
          }
        }}
      >
        {GridItem}
      </FixedSizeGrid>
    </Root>
  );
};



export default memo(CustomGrid, areEqual);